export function getVisibleHoldingQuantity(item) {
  const settledQuantity = Number(item.quantity) || 0;
  const t1Quantity = Number(item.t1_quantity) || 0;
  return settledQuantity + t1Quantity;
}

function getPositionRiskAmount(item) {
  const quantity = Math.abs(Number(item.quantity ?? item.net_quantity) || 0);
  const entryPrice = Number(item.entry_price ?? item.avg_price ?? item.average_price);
  const stoplossPrice = Number(item.trailing_stoploss_price ?? item.initial_stoploss_price ?? item.saved_stoploss);
  const livePrice = Number(item.last_price ?? item.close_price);
  if (!quantity) return 0;
  if (!Number.isFinite(entryPrice) || !Number.isFinite(stoplossPrice) || entryPrice <= 0 || stoplossPrice <= 0) return 0;
  const plannedRisk = Math.abs(entryPrice - stoplossPrice) * quantity;
  if (!Number.isFinite(livePrice) || livePrice <= 0) return plannedRisk;
  const liveRisk = Math.abs(livePrice - stoplossPrice) * quantity;
  return Number.isFinite(liveRisk) ? liveRisk : plannedRisk;
}

function getHoldingRiskAmount(item) {
  const quantity = Math.abs(getVisibleHoldingQuantity(item));
  const entryPrice = Number(item.average_price);
  const stoplossPrice = Number(item.saved_stoploss);
  const livePrice = Number(item.last_price ?? item.close_price);
  if (!quantity) return 0;
  if (!Number.isFinite(entryPrice) || !Number.isFinite(stoplossPrice) || entryPrice <= 0 || stoplossPrice <= 0) return 0;
  const plannedRisk = Math.abs(entryPrice - stoplossPrice) * quantity;
  if (!Number.isFinite(livePrice) || livePrice <= 0) return plannedRisk;
  const liveRisk = Math.abs(livePrice - stoplossPrice) * quantity;
  return Number.isFinite(liveRisk) ? liveRisk : plannedRisk;
}

export function getRealSummaryMetrics({ holdings = [], positions = [], realSummary = null } = {}) {
  const netPositions = (Array.isArray(positions) ? positions : (positions.net || []))
    .filter((position) => Number(position.quantity ?? position.net_quantity) !== 0);
  const plannedRiskBreakdown = [];

  const holdingInvestedValue = holdings.reduce((total, item) => total + (Number(item.average_price) || 0) * getVisibleHoldingQuantity(item), 0);
  const holdingCurrentValue = holdings.reduce((total, item) => total + (Number(item.last_price ?? item.close_price) || 0) * getVisibleHoldingQuantity(item), 0);
  const positionInvestedValue = netPositions.reduce((total, item) => total + (Number(item.entry_price ?? item.avg_price ?? item.average_price) || 0) * Math.abs(Number(item.quantity ?? item.net_quantity) || 0), 0);
  const openPositionValue = netPositions.reduce((total, item) => total + (Number(item.last_price ?? item.close_price) || 0) * Math.abs(Number(item.quantity ?? item.net_quantity) || 0), 0);
  const investedValue = holdingInvestedValue + positionInvestedValue;
  const currentValue = holdingCurrentValue + openPositionValue;
  const totalPnl = holdings.reduce((total, item) => total + (Number(item.pnl) || 0), 0) + netPositions.reduce((total, item) => total + (Number(item.pnl) || 0), 0);
  const totalPlannedRisk = netPositions.reduce((total, item) => {
    const quantity = Math.abs(Number(item.quantity ?? item.net_quantity) || 0);
    const entryPrice = Number(item.entry_price ?? item.avg_price ?? item.average_price);
    const stoplossPrice = Number(item.initial_stoploss_price ?? item.saved_stoploss);
    if (!quantity || !Number.isFinite(entryPrice) || !Number.isFinite(stoplossPrice) || entryPrice <= 0 || stoplossPrice <= 0) return total;
    const plannedRisk = Math.abs(entryPrice - stoplossPrice) * quantity;
    plannedRiskBreakdown.push({
      type: 'POSITION',
      symbol: item.tradingsymbol || item.trading_symbol || item.symbol || '-',
      exchange: item.exchange || 'NSE',
      quantity,
      entryPrice,
      stoplossPrice,
      plannedRisk,
    });
    return total + plannedRisk;
  }, 0) + holdings.reduce((total, item) => {
    const quantity = Math.abs(getVisibleHoldingQuantity(item));
    const entryPrice = Number(item.average_price);
    const stoplossPrice = Number(item.saved_stoploss);
    if (!quantity || !Number.isFinite(entryPrice) || !Number.isFinite(stoplossPrice) || entryPrice <= 0 || stoplossPrice <= 0) return total;
    const plannedRisk = Math.abs(entryPrice - stoplossPrice) * quantity;
    plannedRiskBreakdown.push({
      type: 'HOLDING',
      symbol: item.tradingsymbol || item.symbol || '-',
      exchange: item.exchange || 'NSE',
      quantity,
      entryPrice,
      stoplossPrice,
      plannedRisk,
    });
    return total + plannedRisk;
  }, 0);
  const totalLiveRisk = netPositions.reduce((total, item) => total + getPositionRiskAmount(item), 0) + holdings.reduce((total, item) => total + getHoldingRiskAmount(item), 0);
  const rawAvailableCash = Number(realSummary?.available_cash ?? realSummary?.wallet_balance ?? 0);
  const availableBalance = rawAvailableCash;
  const worstCaseWallet = Math.max(rawAvailableCash + investedValue - totalPlannedRisk, 0);

  return {
    totalPnl,
    totalLiveProfit: totalPnl,
    totalPlannedRisk,
    currentValue,
    investedValue,
    worstCaseWallet,
    availableBalance,
    rawAvailableCash,
    totalLiveRisk,
    plannedRiskBreakdown: plannedRiskBreakdown.sort((left, right) => (right.plannedRisk || 0) - (left.plannedRisk || 0)),
    worstCaseWalletBreakdown: {
      cash: rawAvailableCash,
      invested: investedValue,
      plannedRisk: totalPlannedRisk,
    },
  };
}
